import React from 'react';
import { Box, Skeleton, styled, alpha } from '@mui/material';
import { motion } from 'framer-motion';

interface SkeletonLoaderProps {
  variant?: 'card' | 'table' | 'stats' | 'chart';
  count?: number;
}

const StyledSkeleton = styled(Skeleton)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  '&::after': {
    background: `linear-gradient(90deg, transparent, ${alpha(
      theme.palette.primary.main,
      0.05
    )}, transparent)`,
  },
}));

const MotionBox = motion.create(Box);

const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({ variant = 'card', count = 1 }) => {
  const renderSkeleton = () => {
    switch (variant) {
      case 'card':
        return (
          <Box>
            <StyledSkeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
            <Box sx={{ pt: 2 }}>
              <StyledSkeleton width="60%" height={24} />
              <StyledSkeleton width="40%" height={20} sx={{ mt: 1 }} />
            </Box>
          </Box>
        );

      case 'table':
        return (
          <Box>
            <StyledSkeleton variant="rectangular" height={56} sx={{ mb: 1 }} />
            {[...Array(5)].map((_, index) => (
              <StyledSkeleton
                key={index}
                variant="rectangular"
                height={72}
                sx={{ mb: 0.5 }}
              />
            ))}
          </Box>
        );

      case 'stats':
        return (
          <Box sx={{ display: 'flex', gap: 2 }}>
            {[...Array(4)].map((_, index) => (
              <Box key={index} sx={{ flex: 1 }}>
                <StyledSkeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
              </Box>
            ))}
          </Box>
        );

      case 'chart':
        return (
          <Box>
            <StyledSkeleton width="40%" height={32} sx={{ mb: 2 }} />
            <StyledSkeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
          </Box>
        );

      default:
        return <StyledSkeleton variant="rectangular" height={200} />;
    }
  };

  return (
    <>
      {[...Array(count)].map((_, index) => (
        <MotionBox
          key={index}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: index * 0.1 }}
          sx={{ mb: 2 }}
        >
          {renderSkeleton()}
        </MotionBox>
      ))}
    </>
  );
};

export default SkeletonLoader;